import React from "react";
import { Divider, TableBody, Table, TableContainer, TableHead, TableRow, TableCell, Typography } from "@mui/material";

import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { WorkshopInstanceDto } from "../../../types/workshopTypes";

interface WorkshopInstanceTableProps {
  workshopInstances: WorkshopInstanceDto[];
}

/**
 * Displays a table of workshop instances.
 * @param workshopInstances The workshop instances to display.
 * @returns A table of workshop instances.
 */
const WorkshopInstanceTable: React.FunctionComponent<WorkshopInstanceTableProps> = ({
  workshopInstances,
}: WorkshopInstanceTableProps) => {
  // Calculate the average note of all workshop instances.
  const validWorkshops = workshopInstances.filter(
    (workshop) => workshop.calculatedGrade !== null && workshop.calculatedGrade !== undefined
  );
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const sum = validWorkshops.reduce((acc, curr) => acc + curr.calculatedGrade!, 0);
  const average = validWorkshops.length > 0 ? sum / validWorkshops.length : "-";

  return (
    <>
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Typography variant="h6" component="h2" gutterBottom fontWeight={"bold"}>
        Workshop-Termine
      </Typography>
      <TableContainer sx={{ mb: 5, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold", border: "1px solid white" }}>Datum</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", border: "1px solid white" }}>Status</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", border: "1px solid white" }}>Referenten</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", border: "1px solid white" }}># Teilnehmer</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", border: "1px solid white" }}>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workshopInstances.map((workshopInstance) => (
              <TableRow
                component={Link}
                to={`/workshops/${workshopInstance.workshopId}/${workshopInstance.workshopInstanceId}`}
                key={workshopInstance.workshopInstanceId}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <TableCell sx={{ border: "1px solid grey", borderTop: 0, borderLeft: 0 }}>
                  {dayjs(workshopInstance.date).format("DD.MM.YYYY")}
                  {dayjs(workshopInstance.startTime).isValid() && dayjs(workshopInstance.endTime).isValid()
                    ? ` / ${dayjs(workshopInstance.startTime).format("HH:mm")} - ${dayjs(
                        workshopInstance.endTime
                      ).format("HH:mm")}`
                    : " "}
                </TableCell>
                <TableCell sx={{ border: "1px solid grey", borderTop: 0 }}>{workshopInstance.status}</TableCell>
                <TableCell sx={{ border: "1px solid grey", borderTop: 0 }}>
                  {workshopInstance.referents
                    .map((referent) => `${referent.firstname} ${referent.lastname}`)
                    .join(", ")}
                </TableCell>
                <TableCell sx={{ border: "1px solid grey", borderTop: 0 }}>
                  {workshopInstance.participants.length}
                </TableCell>
                <TableCell sx={{ border: "1px solid grey", borderTop: 0, borderRight: 0 }}>
                  {workshopInstance.calculatedGrade ? workshopInstance.calculatedGrade : "-"}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{ fontWeight: "bold", border: "1px solid grey", borderTop: 0, borderLeft: 0, borderRight: 0 }}
              >
                Durchschnitt
              </TableCell>
              <TableCell sx={{ border: "1px solid grey", borderTop: 0, borderLeft: 0, borderRight: 0 }} />
              <TableCell sx={{ border: "1px solid grey", borderTop: 0, borderLeft: 0, borderRight: 0 }} />
              <TableCell sx={{ border: "1px solid grey", borderTop: 0, borderLeft: 0, borderRight: 0 }} />
              <TableCell
                sx={{ fontWeight: "bold", border: "1px solid grey", borderTop: 0, borderLeft: 0, borderRight: 0 }}
              >
                {average}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default WorkshopInstanceTable;
